import { Icon } from '../../utils/icons';

const AppointmentNotifications = ({ config, onConfigChange }) => {
    const notifications = config.notifications || {};

    const clientConfirmation = notifications.clientConfirmation !== false; // Default to true
    const ownerNotification = notifications.ownerNotification !== false; // Default to true
    const ownerEmail = notifications.ownerEmail || '';

    const handleNotificationToggle = (type, value) => {
        onConfigChange({
            notifications: {
                ...notifications,
                [type]: value
            }
        });
    };

    const handleOwnerEmailChange = (email) => {
        onConfigChange({
            notifications: {
                ...notifications,
                ownerEmail: email
            }
        });
    };

    return (
        <div className="p-4 max-h-full overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Email Notifications</h3>
            <p className="text-sm text-gray-400 mb-6">
                Configure automatic email notifications for appointments
            </p>

            <div className="space-y-8">
                {/* Client Confirmation */}
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Client Notifications</h4>

                    <div className="space-y-4">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                    <Icon name="mail" size={16} className="text-blue-400" />
                                    <h5 className="font-medium text-white text-sm">Booking Confirmation to Client</h5>
                                </div>
                                <p className="text-xs text-gray-400 leading-relaxed">
                                    Sends a confirmation email to clients with appointment details upon booking.
                                </p>

                                {clientConfirmation && (
                                    <div className="mt-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                                        <h6 className="text-xs font-medium text-gray-300 mb-2">Email will include:</h6>
                                        <ul className="text-xs text-gray-400 space-y-1">
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Appointment date and time</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Service details and duration</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Business contact information</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Cancellation and rescheduling instructions</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                                <input
                                    type="checkbox"
                                    checked={clientConfirmation}
                                    onChange={(e) => handleNotificationToggle('clientConfirmation', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Owner Notification */}
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Owner Notifications</h4>

                    <div className="space-y-4">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                    <Icon name="notification" size={16} className="text-orange-400" />
                                    <h5 className="font-medium text-white text-sm">Owner Booking Notification</h5>
                                </div>
                                <p className="text-xs text-gray-400 leading-relaxed">
                                    Notifies the business owner via email when a new appointment is booked.
                                </p>

                                {ownerNotification && (
                                    <div className="mt-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                                        <h6 className="text-xs font-medium text-gray-300 mb-2">Notification will include:</h6>
                                        <ul className="text-xs text-gray-400 space-y-1">
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Customer contact information</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Requested service and time</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Booking timestamp</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                                                <span>Customer notes (if provided)</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                                <input
                                    type="checkbox"
                                    checked={ownerNotification}
                                    onChange={(e) => handleNotificationToggle('ownerNotification', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        {/* Owner Email Input */}
                        {ownerNotification && (
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Owner Email Address *
                                </label>
                                <input
                                    type="email"
                                    placeholder="owner@business.com"
                                    value={ownerEmail}
                                    onChange={(e) => handleOwnerEmailChange(e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    New booking notifications will be sent to this email address
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Email Template Preview */}
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Email Preview</h4>

                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <div className="space-y-4">
                            {/* Client Email Preview */}
                            {clientConfirmation && (
                                <div>
                                    <h6 className="text-xs font-medium text-blue-400 mb-2">Client Confirmation Email</h6>
                                    <div className="bg-white text-gray-800 rounded p-3 text-xs">
                                        <div className="border-b border-gray-200 pb-2 mb-2">
                                            <div className="font-semibold">Appointment Confirmed ✅</div>
                                            <div className="text-gray-600">Thank you for booking with us!</div>
                                        </div>
                                        <div className="space-y-1">
                                            <div><strong>Service:</strong> Men&apos;s Haircut</div>
                                            <div><strong>Date & Time:</strong> Sept 16, 2024 at 2:00 PM</div>
                                            <div><strong>Duration:</strong> 30 minutes</div>
                                            <div><strong>Location:</strong> Elite Cuts Salon</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Owner Email Preview */}
                            {ownerNotification && (
                                <div>
                                    <h6 className="text-xs font-medium text-orange-400 mb-2">Owner Notification Email</h6>
                                    <div className="bg-white text-gray-800 rounded p-3 text-xs">
                                        <div className="border-b border-gray-200 pb-2 mb-2">
                                            <div className="font-semibold">New Appointment Booked 📅</div>
                                            <div className="text-gray-600">You have a new booking!</div>
                                        </div>
                                        <div className="space-y-1">
                                            <div><strong>Customer:</strong> John Smith</div>
                                            <div><strong>Service:</strong> Men&apos;s Haircut</div>
                                            <div><strong>Date & Time:</strong> Sept 16, 2024 at 2:00 PM</div>
                                            <div><strong>Email:</strong> john@example.com</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!clientConfirmation && !ownerNotification && (
                                <div className="text-center py-4 text-gray-500">
                                    <Icon name="mail" size={32} className="mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Enable notifications to see email previews</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Best Practices */}
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-3">📧 Email Best Practices</h4>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <ul className="space-y-2 text-xs text-gray-400">
                            <li className="flex items-start space-x-2">
                                <Icon name="info" size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <span>Emails are sent immediately after successful booking</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <Icon name="info" size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <span>Client emails include booking confirmation and cancellation links</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <Icon name="info" size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <span>Owner notifications help you stay on top of new bookings</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <Icon name="info" size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <span>All emails are mobile-friendly and professionally designed</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentNotifications;
